import { useRef } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { showErrorToast } from "app/actions/toast_actions";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { translate } from "app/filters/i18nFilter";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

import type { AssignToUserRequestParams } from "../../types/common";
import { assignToUsersQueryKeys } from "../key-factory/factory";

type BaseParams = Pick<AssignToUserRequestParams, "assigneeId"> & Partial<Pick<AssignToUserRequestParams, "domains">>;

type FetchFn<Params extends BaseParams> = (params: Params) => Promise<ReadonlyArray<string>>;

export const useAssignDomainsToUserMutation = <Params extends BaseParams>(fetchDomains?: FetchFn<Params>) => {
    const queryClient = useQueryClient();

    const domains = useRef<readonly string[]>([]);

    return useMutation<readonly string[], unknown, Params>({
        async mutationFn(params) {
            await salesApiService.assignToUsers.assignWebsitesToUser({
                ...params,
                domains: domains.current,
            });

            return domains.current;
        },
        async onMutate(params) {
            domains.current = fetchDomains ? await fetchDomains(params) : (params.domains ?? []);
        },
        async onSuccess() {
            await queryClient.invalidateQueries({
                queryKey: assignToUsersQueryKeys.assignableUsersList(),
            });
        },
        onError() {
            const toast = getToastItemComponent({
                text: translate("si.common.toast.error"),
            });

            showErrorToast(toast);
        },
    });
};
